import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import useAccess from "../../hooks/useAccess";
import { useStore } from "../../context/StoreContext";
import {
  Activity, Building2, CalendarDays, CheckCircle2Icon, Crown,
  IndianRupee, Loader2, RefreshCcw, Settings, Store, UsersRound,
  WalletCards, MinusCircle, CalendarCheck, PackageOpen, PackageCheck,
} from "lucide-react";
import styles from "./DashboardEnterprise.module.css";
import TrendGraph from "../Dashboard/components/TrendGraph/TrendGraph";
import CollectionBreakdown from "../Dashboard/components/CollectionBreakdown/CollectionBreakdown";
import GlobalDateFilter from "../../components/GlobalDateFilter/GlobalDateFilter";
import { getBillDate, toLocalYMD } from "../../utils/billDates";
import { API_ROOT } from "../../config/api";
import { clientMessage, openWhatsApp, prettyTime } from "../../utils/appointmentMessages";
import ax from "./DashboardAppointments.module.css";
import LiveFeed, { buildLiveFeed } from "./LiveFeed/LiveFeed";

// =============================================================================
// Store + Enterprise (HQ) dashboard.
// Every completed bill is counted on the day it was COMPLETED (completedAt),
// same as Dashboard, Invoice history, Booking history and Analytics.
// Appointments keep their own booked time.
//
// Packages:
//   "Packages sold"         = package lines sold on bills (shop revenue, counted once)
//   "Package services used" = services given from client packages (paid earlier, ₹0 on the bill).
//                             Their value (share of the package price) is staff credit, NOT revenue again.
// =============================================================================

const INVOICES_API = `${API_ROOT}/api/v1/smiify/sm/invoices`;
const APPOINTMENTS_API = `${API_ROOT}/api/v1/smiify/sm/appointments`;

const APPT_INACTIVE = ["Cancelled", "No_Show"];

// Theme colours (purple / green / pink / greys)
const C = {
  primary: "var(--h-color-primary-600, #673de6)",
  primarySoft: "var(--h-color-primary-50, #f5f6ff)",
  primaryLight: "var(--h-color-primary-300, #bcbdff)",
  primaryDeep: "var(--h-color-primary-800, #331c74)",
  green: "var(--h-color-success-600, #007a49)",
  greenSoft: "var(--h-color-success-50, #f1f9f3)",
  greenMid: "var(--h-color-success-500, #009e5b)",
  pink: "var(--danger--500, #fc5185)",
  pinkSoft: "var(--danger--50, #fff3f7)",
  danger: "var(--h-color-danger-600, #be1025)",
  grey: "var(--h-color-neutral-600, #58585e)",
  greyLight: "var(--h-color-neutral-400, #9d9ea4)",
  muted: "var(--text-secondary, #58585e)",
  canvas: "var(--h-color-neutral-50, #f8f9fa)",
};

const MEMBERSHIP_TYPES = ["package", "prepaid", "discount", "membership"];

const numberValue = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (value === null || value === undefined || value === "") return 0;
  const parsed = Number(String(value).replace(/[₹,\s]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const textValue = (value, fallback = "") => {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "string" || typeof value === "number") return String(value);
  return fallback;
};

const money = (value) => `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 0 }).format(numberValue(value))}`;

const getTodayDate = () => toLocalYMD(new Date());

/** 9876543210 -> 98••••••10 */
const maskPhone = (p) => {
  const s = String(p || "");
  if (!s) return "";
  return s.length >= 6 ? `${s.slice(0, 2)}${"•".repeat(s.length - 4)}${s.slice(-2)}` : "••••";
};

/* ----------------------------- dates ----------------------------- */
/** Appointment time (date-only values at 00:00 UTC fall back to the real creation time) */
const getAppointmentTime = (doc = {}) => {
  // Current bookings: date "YYYY-MM-DD" + startTime "HH:mm" (salon time)
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(doc.date || "")) && /^\d{1,2}:\d{2}$/.test(String(doc.startTime || ""))) {
    const [y, m, d] = doc.date.split("-").map(Number);
    const [hh, mm] = doc.startTime.split(":").map(Number);
    return new Date(y, m - 1, d, hh, mm);
  }
  if (doc.date && String(doc.date).includes("T00:00:00.000Z") && doc.createdAt && !doc.appointmentDate && !doc.startTime) return new Date(doc.createdAt);
  const date = new Date(doc.appointmentDate || doc.startTime || doc.scheduledAt || doc.date || doc.createdAt || 0);
  return Number.isNaN(date.getTime()) ? new Date(0) : date;
};

/** Bill time = completion moment */
const getInvoiceTime = (invoice) => {
  const d = getBillDate(invoice);
  return Number.isNaN(d.getTime()) ? new Date(0) : d;
};

const formatTime = (date) => (!date || date.getTime() === 0 ? "—" : new Intl.DateTimeFormat("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }).format(date));
const formatDate = (date) => (!date || date.getTime() === 0 ? "—" : new Intl.DateTimeFormat("en-IN", { day: "2-digit", month: "short", year: "numeric" }).format(date));

const getRangeDates = (range) => {
  const s = String(range?.startDate || getTodayDate()).split("-").map(Number);
  const e = String(range?.endDate || range?.startDate || getTodayDate()).split("-").map(Number);
  return {
    start: new Date(s[0], s[1] - 1, s[2], 0, 0, 0, 0),
    end: new Date(e[0], e[1] - 1, e[2], 23, 59, 59, 999),
  };
};

/* ----------------------------- stores ----------------------------- */
const getStoreId = (store) => textValue(store?._id ?? store?.id ?? store?.storeId ?? store?.value);
const getStoreName = (store, fallback = "Unassigned store") => textValue(store?.storeName ?? store?.name ?? store?.businessName ?? store?.title ?? store?.label, fallback);

const isHeadOffice = (store) => {
  const flags = [store?.isHQ, store?.isHq, store?.isHeadOffice, store?.isEnterprise, store?.isEnterpriseHQ, store?.isAllStores, store?.isMainAccount, store?.isParentStore, store?.scope === "all", store?.storeType === "HQ", store?.storeType === "ENTERPRISE", store?.type === "HQ", store?.type === "ENTERPRISE", store?.accountType === "enterprise", store?.accountType === "hq"];
  const text = [store?.storeType, store?.type, store?.accountType, store?.role, store?.kind, store?.storeName, store?.name].filter(Boolean).join(" ").toLowerCase();
  return flags.some(Boolean) || text.includes("head office") || text.includes("main account") || text.includes("enterprise") || /\bhq\b/.test(text);
};

const getEnterpriseId = (store) => textValue(store?.enterpriseId ?? store?.organizationId ?? store?.businessId ?? store?.accountId ?? store?.parentId);

const getRecordStore = (record, fallbackStore = null) => {
  const source = record?.__dashboardStore || record?.store || record?.location || record?.branch;
  const obj = source && typeof source === "object" ? source : {};
  const id = textValue(record?.__dashboardStoreId ?? record?.storeId ?? record?.locationId ?? record?.branchId ?? obj?._id ?? obj?.id ?? (typeof source === "string" ? source : undefined) ?? getStoreId(fallbackStore), "unassigned");
  const name = textValue(record?.__dashboardStoreName ?? record?.storeName ?? record?.locationName ?? record?.branchName ?? obj?.storeName ?? obj?.name ?? getStoreName(fallbackStore), "Unassigned store");
  return { id: String(id), name };
};

/* ----------------------------- records ----------------------------- */
const getCustomer = (record) => {
  const source = record?.customer ?? record?.client ?? record?.customerDetails;
  const customer = source && typeof source === "object" ? source : {};
  return {
    name: textValue(customer?.name ?? customer?.fullName ?? (typeof source === "string" ? source : undefined) ?? record?.customerName ?? record?.clientName, "Walk-in customer"),
    phone: textValue(customer?.phone ?? customer?.mobile ?? record?.customerPhone ?? record?.phone, ""),
    id: textValue(customer?.customerId ?? customer?._id ?? customer?.id ?? record?.customerId ?? record?.clientId ?? customer?.phone ?? customer?.mobile ?? customer?.name, "walk-in"),
    isNew: Boolean(customer?.isNew ?? customer?.newCustomer ?? record?.isNewCustomer ?? record?.newCustomer),
    createdAt: customer?.createdAt ?? record?.customerCreatedAt,
  };
};

const getInvoiceTotal = (invoice) => numberValue(invoice?.summary?.grandTotal ?? invoice?.summary?.total ?? invoice?.grandTotal ?? invoice?.totalAmount ?? invoice?.netAmount ?? invoice?.total ?? invoice?.amount);

/** Staff on a line: [{ name }] array (current bills), a single object, or plain strings */
const peopleNames = (value) => {
  const list = Array.isArray(value) ? value : value ? [value] : [];
  return list.map((p) => (typeof p === "object" ? textValue(p?.name ?? p?.fullName ?? p?.staffName) : textValue(p))).map((n) => n.trim()).filter(Boolean);
};

const getInvoiceItems = (invoice) => {
  const source = invoice?.items ?? invoice?.services ?? invoice?.serviceDetails ?? invoice?.lineItems ?? [];
  const list = Array.isArray(source) ? source : source ? [source] : [];
  return list.map((item, index) => ({
    name: textValue(item?.itemName ?? item?.name ?? item?.title ?? item?.serviceName, `Service ${index + 1}`),
    type: String(item?.itemType || "service").toLowerCase(),
    quantity: numberValue(item?.qty ?? item?.quantity) || 1,
    amount: numberValue(item?.amount ?? item?.total ?? item?.totalAmount ?? item?.price ?? item?.subtotal),
    staff: peopleNames(item?.staff ?? item?.staffName ?? item?.servedBy),
    // Service given from a client package (₹0 here; value = its share of the package price)
    fromPackage: Boolean(item?.packageRedeem?.packageId),
    packageValue: numberValue(item?.packageRedeem?.value),
    packageName: textValue(item?.packageRedeem?.packageName),
  }));
};

const getPaymentBreakdown = (invoice) => {
  const p = invoice?.payments && typeof invoice.payments === "object" && !Array.isArray(invoice.payments) ? invoice.payments : {};
  const result = { cash: 0, upi: 0, card: 0, prepaid: 0, wallet: 0, other: 0 };

  if (Array.isArray(invoice?.payments)) {
    invoice.payments.forEach((pay) => {
      const mode = String(pay?.mode ?? pay?.method ?? "").toLowerCase();
      const amt = numberValue(pay?.amount ?? pay?.paidAmount);
      if (mode.includes("cash")) result.cash += amt;
      else if (mode.includes("upi") || mode.includes("online")) result.upi += amt;
      else if (mode.includes("prepaid") || mode.includes("member")) result.prepaid += amt;
      else if (mode.includes("wallet")) result.wallet += amt;
      else if (mode.includes("card")) result.card += amt;
      else result.other += amt;
    });
  } else {
    result.cash = numberValue(p.cash);
    result.upi = numberValue(p.upi);
    result.card = numberValue(p.card);
    result.wallet = numberValue(p.wallet);
    result.prepaid = numberValue(p.prepaidCardAmount ?? p.prepaidAmount ?? p.membershipAmount);
    result.other = numberValue(p.other);
  }
  return { ...result, actualCollection: result.cash + result.upi + result.card + result.other };
};

const getAppointmentStatus = (appointment) => textValue(appointment?.status ?? appointment?.appointmentStatus, "Scheduled").replace(/_/g, " ");

const unwrapArray = (payload) => {
  const candidates = [payload?.data?.rows, payload?.data?.invoices, payload?.data?.appointments, payload?.data?.items, payload?.data?.data, payload?.data, payload?.rows, payload?.invoices, payload?.appointments, payload?.items];
  return candidates.find(Array.isArray) || [];
};

const storeListFromContext = (ctx, activeStore) => {
  const candidates = [ctx?.stores, ctx?.allStores, ctx?.storeList, ctx?.availableStores, ctx?.storeOptions, activeStore?.stores, activeStore?.childStores, activeStore?.branches, activeStore?.locations, activeStore?.children, activeStore?.storeIds, activeStore?.childStoreIds, activeStore?.locationIds];
  const list = candidates.find((c) => Array.isArray(c) && c.length) || [];
  const seen = new Set();
  return list
    .map((s) => (typeof s === "object" ? s : { id: s, name: String(s) }))
    .filter((s) => getStoreId(s) && !isHeadOffice(s))
    .filter((s) => { const id = getStoreId(s); if (seen.has(id)) return false; seen.add(id); return true; });
};

const markedRows = (rows, target, kind) => rows.map((row) => ({
  ...row,
  __dashboardRecordType: kind,
  __dashboardStoreId: row?.storeId ?? row?.locationId ?? row?.branchId ?? getStoreId(target),
  __dashboardStoreName: row?.storeName ?? row?.locationName ?? row?.branchName ?? getStoreName(target),
  __dashboardStore: target,
}));

const requestRows = async (endpoint, params, token, signal, storeId) => {
  const response = await fetch(`${endpoint}?${new URLSearchParams(params).toString()}`, {
    signal,
    headers: { Authorization: `Bearer ${token}`, ...(storeId ? { "x-store-id": String(storeId) } : {}) },
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok || result.success === false) {
    throw new Error(result.message || `Unable to load ${endpoint.includes("invoices") ? "invoices" : "appointments"}.`);
  }
  return unwrapArray(result);
};

const createStoreStats = (target) => ({
  id: getStoreId(target) || "unassigned",
  name: getStoreName(target),
  revenue: 0, actualCollection: 0, prepaid: 0, wallet: 0, membershipSales: 0,
  packagesSold: 0, packageServicesValue: 0, packageServicesCount: 0,
  invoiceCount: 0, appointmentCount: 0, completedAppointments: 0,
  customers: new Set(), topStaff: "—", topStaffSales: 0,
});

/* ============================== calculations ============================== */
const buildDashboardData = ({ invoices, appointments, dateRange, isHQ, storeTargets }) => {
  const { start, end } = getRangeDates(dateRange);
  const inRange = (d) => d >= start && d <= end;

  const rangeInvoices = invoices
    .map((inv) => ({ ...inv, __at: getInvoiceTime(inv) }))
    .filter((inv) => inRange(inv.__at))
    .sort((a, b) => b.__at - a.__at);
  const rangeAppointments = appointments
    .map((apt) => ({ ...apt, __at: getAppointmentTime(apt) }))
    .filter((apt) => inRange(apt.__at))
    .sort((a, b) => a.__at - b.__at);

  const pay = { cash: 0, upi: 0, card: 0, prepaid: 0, wallet: 0, other: 0, actualCollection: 0 };
  const serviceMap = new Map();
  const staffMap = new Map();
  const storeMap = new Map();
  const customerSet = new Set();
  const newCustomerSet = new Set();
  let membershipSales = 0;           // prepaid + discount plans
  let packagesSold = 0;              // package lines sold (revenue)
  let packagesSoldCount = 0;
  let packageServicesValue = 0;      // services given from packages (staff credit, not revenue)
  let packageServicesCount = 0;
  let unassignedSales = 0;

  storeTargets.forEach((t) => storeMap.set(getStoreId(t), createStoreStats(t)));

  rangeInvoices.forEach((invoice) => {
    const store = getRecordStore(invoice, storeTargets.length === 1 ? storeTargets[0] : null);
    if (!storeMap.has(store.id)) storeMap.set(store.id, createStoreStats({ id: store.id, name: store.name }));
    const st = storeMap.get(store.id);
    const total = getInvoiceTotal(invoice);
    const payment = getPaymentBreakdown(invoice);
    const customer = getCustomer(invoice);
    const customerKey = `${store.id}:${customer.id}`;

    st.revenue += total;
    st.actualCollection += payment.actualCollection;
    st.prepaid += payment.prepaid;
    st.wallet += payment.wallet;
    st.invoiceCount += 1;
    st.customers.add(customerKey);
    customerSet.add(customerKey);
    if (customer.isNew || (customer.createdAt && inRange(new Date(customer.createdAt)))) newCustomerSet.add(customerKey);
    Object.keys(pay).forEach((k) => { pay[k] += payment[k] || 0; });

    const items = getInvoiceItems(invoice);
    // Bill discount + round off spread over lines so line totals match the bill (package services are ₹0 lines)
    const itemSum = items.reduce((s, it) => s + it.amount, 0);
    const factor = itemSum > 0 ? total / itemSum : 1;
    const countedBills = new Set();
    const paidLines = items.filter((it) => !it.fromPackage).length || 1;

    const creditStaff = (names, value, qty, { fromPackage = false } = {}) => {
      const share = value / names.length; // shared line: split equally
      names.forEach((name) => {
        const key = `${store.id}::${name.toLowerCase()}`;
        const person = staffMap.get(key) || { name, storeId: store.id, storeName: store.name, bills: 0, sales: 0, services: 0, packageCredit: 0, packageServices: 0 };
        if (!countedBills.has(key)) { person.bills += 1; countedBills.add(key); }
        person.services += qty / names.length;
        if (fromPackage) {
          person.packageCredit += share;
          person.packageServices += qty / names.length;
        } else {
          person.sales += share;
        }
        staffMap.set(key, person);
      });
    };

    items.forEach((item) => {
      // ---- service given from a client package: count it, credit the staff, no revenue ----
      if (item.fromPackage) {
        packageServicesValue += item.packageValue;
        packageServicesCount += item.quantity;
        st.packageServicesValue += item.packageValue;
        st.packageServicesCount += item.quantity;
        const svc = serviceMap.get(item.name) || { name: item.name, bookings: 0, revenue: 0, fromPackages: 0 };
        svc.bookings += item.quantity;
        svc.fromPackages = (svc.fromPackages || 0) + item.quantity;
        serviceMap.set(item.name, svc);
        if (item.staff.length) creditStaff(item.staff, item.packageValue, item.quantity, { fromPackage: true });
        return;
      }

      const lineTotal = itemSum > 0 ? item.amount * factor : total / paidLines;

      if (item.type === "package") {
        packagesSold += lineTotal;
        packagesSoldCount += item.quantity;
        st.packagesSold += lineTotal;
        st.membershipSales += lineTotal;
      } else if (MEMBERSHIP_TYPES.includes(item.type)) {
        membershipSales += lineTotal;
        st.membershipSales += lineTotal;
      } else {
        const svc = serviceMap.get(item.name) || { name: item.name, bookings: 0, revenue: 0, fromPackages: 0 };
        svc.bookings += item.quantity;
        svc.revenue += lineTotal;
        serviceMap.set(item.name, svc);
      }

      if (!item.staff.length) {
        unassignedSales += lineTotal;
        return;
      }
      creditStaff(item.staff, lineTotal, item.quantity);
    });
  });

  rangeAppointments.forEach((appointment) => {
    const store = getRecordStore(appointment, storeTargets.length === 1 ? storeTargets[0] : null);
    if (!storeMap.has(store.id)) storeMap.set(store.id, createStoreStats({ id: store.id, name: store.name }));
    const st = storeMap.get(store.id);
    st.appointmentCount += 1;
    if (getAppointmentStatus(appointment).toLowerCase().includes("complete")) st.completedAppointments += 1;
    const customer = getCustomer(appointment);
    const customerKey = `${store.id}:${customer.id}`;
    customerSet.add(customerKey);
    if (customer.isNew || (customer.createdAt && inRange(new Date(customer.createdAt)))) newCustomerSet.add(customerKey);
  });

  const storeRevenueTotal = [...storeMap.values()].reduce((s, x) => s + x.revenue, 0);
  // Ranked by total credit = own sales + services given from client packages
  const staffStats = [...staffMap.values()]
    .map((p) => ({ ...p, credit: p.sales + p.packageCredit }))
    .sort((a, b) => b.credit - a.credit);
  const storeStats = [...storeMap.values()]
    .map((x) => {
      const top = staffStats.find((p) => p.storeId === x.id);
      return {
        ...x,
        customerCount: x.customers.size,
        avgTicket: x.invoiceCount ? x.revenue / x.invoiceCount : 0,
        salesShare: storeRevenueTotal ? (x.revenue / storeRevenueTotal) * 100 : 0,
        appointmentCompletion: x.appointmentCount ? (x.completedAppointments / x.appointmentCount) * 100 : 0,
        topStaff: top?.name || "—",
        topStaffSales: top?.credit || 0,
      };
    })
    .sort((a, b) => b.revenue - a.revenue);

  const serviceStats = [...serviceMap.values()].sort((a, b) => b.revenue - a.revenue || b.bookings - a.bookings).slice(0, 8);
  const maxServiceRevenue = serviceStats[0]?.revenue || 1;
  const services = serviceStats.map((s) => ({ ...s, share: Math.round((s.revenue / maxServiceRevenue) * 100) }));

  // Trend: last 30 days by completion day (independent of the date filter)
  const trendMap = {};
  invoices.forEach((inv) => {
    const key = toLocalYMD(getInvoiceTime(inv));
    trendMap[key] = (trendMap[key] || 0) + getInvoiceTotal(inv);
  });
  const trendData = [];
  for (let i = 29; i >= 0; i -= 1) {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - i);
    trendData.push({ name: new Intl.DateTimeFormat("en-IN", { day: "2-digit", month: "short" }).format(d), revenue: trendMap[toLocalYMD(d)] || 0 });
  }

  const splitTotal = pay.actualCollection + pay.prepaid + pay.wallet;
  const collections = [
    { method: "UPI / QR", amount: pay.upi, color: C.primary },
    { method: "Cash", amount: pay.cash, color: C.greenMid },
    { method: "Card (POS)", amount: pay.card, color: C.primaryDeep },
    { method: "Prepaid cards", amount: pay.prepaid, color: C.pink },
    { method: "Khata wallet", amount: pay.wallet, color: C.grey },
    { method: "Other", amount: pay.other, color: C.greyLight },
  ]
    .filter((x) => x.amount > 0)
    .map((x) => ({ ...x, percent: splitTotal ? (x.amount / splitTotal) * 100 : 0, amount: money(x.amount) }));

  // Appointment stats for the selected range
  const nowTs = Date.now();
  const appt = { total: rangeAppointments.length, upcoming: 0, inService: 0, done: 0, cancelled: 0, bookedValue: 0, next: null };
  rangeAppointments.forEach((a) => {
    const active = !APPT_INACTIVE.includes(a.status);
    if (active) appt.bookedValue += numberValue(a.finalAmount ?? a.amount);
    if (a.status === "Completed") appt.done += 1;
    else if (a.status === "In_Service") appt.inService += 1;
    else if (!active) appt.cancelled += 1;
    else if (a.__at.getTime() >= nowTs - 15 * 60000) {
      appt.upcoming += 1;
      if (!appt.next || a.__at < appt.next.__at) appt.next = a;
    }
  });

  const totalRevenue = rangeInvoices.reduce((s, inv) => s + getInvoiceTotal(inv), 0);
  const metrics = {
    totalRevenue,
    revenueWithoutMembership: totalRevenue - pay.prepaid - pay.wallet,
    membershipUsed: pay.prepaid + pay.wallet,
    membershipSales,
    packagesSold,
    packagesSoldCount,
    packageServicesValue,
    packageServicesCount,
    freshCollection: pay.actualCollection,
    unassignedSales,
    appointments: rangeAppointments.length,
    newCustomers: newCustomerSet.size,
    footfall: customerSet.size,
  };

  return {
    isHQ, rangeInvoices, rangeAppointments, metrics, totalRevenue, appt,
    averageTicket: rangeInvoices.length ? totalRevenue / rangeInvoices.length : 0,
    paymentTotals: pay, collections, services, trendData, storeStats, staffStats,
    activeStoreCount: storeStats.filter((x) => x.invoiceCount > 0 || x.appointmentCount > 0).length,
    totalCustomerCount: customerSet.size,
  };
};

/* ============================== UI pieces ============================== */
const SurfacePanel = ({ title, subtitle, action, children, style = {}, noPadding = false }) => (
  <section className={styles.surfacePanel} style={style}>
    <div className={styles.panelHeader}>
      <div>
        <h3 className={styles.panelTitle}>{title}</h3>
        {subtitle && <p className={styles.panelSubtitle}>{subtitle}</p>}
      </div>
      {action && <span className={styles.panelActionBadge}>{action}</span>}
    </div>
    <div className={`${styles.panelContent} ${noPadding ? styles.noPadding : ""}`}>{children}</div>
  </section>
);

const TONES = {
  primary: { color: C.primary, bg: C.primarySoft },
  green: { color: C.green, bg: C.greenSoft },
  pink: { color: C.pink, bg: C.pinkSoft },
  grey: { color: C.grey, bg: C.canvas },
};

const HqMetric = ({ icon: Icon, label, value, note, tone = "primary" }) => {
  const theme = TONES[tone] || TONES.primary;
  return (
    <div className={styles.metricCard}>
      <div className={styles.metricTop}>
        <span className={styles.metricIconWrap} style={{ background: theme.bg, color: theme.color }}>
          <Icon size={14} strokeWidth={2.5} />
        </span>
        <span className={styles.metricNote}>{note}</span>
      </div>
      <div className={styles.metricBottom}>
        <span className={styles.metricLabel}>{label}</span>
        <strong className={styles.metricValue}>{value}</strong>
      </div>
    </div>
  );
};

const EmptyState = ({ message }) => <div className={styles.emptyStateContainer}>{message}</div>;

const ErrorState = ({ message, onRetry }) => (
  <div className={styles.errorStateContainer}>
    <span>{message}</span>
    <button type="button" onClick={onRetry} className={styles.errorRetryBtn}>Retry</button>
  </div>
);

const RecentClientsPanel = ({ invoices, showPhone = true }) => {
  const recent = invoices.slice(0, 7); // already newest first (by completion time)
  return (
    <SurfacePanel title="Recent clients" subtitle="Latest completed bills." noPadding>
      {recent.length === 0 ? <EmptyState message="No completed bills in this period." /> : (
        <div className={styles.tableWrapper}>
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th className={styles.textLeft}>Client</th>
                <th className={styles.textLeft}>Services</th>
                <th className={styles.textRight}>Bill</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((inv, idx) => {
                const customer = getCustomer(inv);
                const items = getInvoiceItems(inv);
                const fromPackage = items.some((i) => i.fromPackage);
                return (
                  <tr key={inv._id || idx}>
                    <td className={styles.textLeft}>
                      <strong className={styles.tdTitle}>{customer.name}</strong>
                      <small className={styles.tdSub}>{customer.phone ? (showPhone ? customer.phone : maskPhone(customer.phone)) : "Walk-in"}</small>
                    </td>
                    <td className={styles.textLeft}>
                      <span className={styles.tdSub} style={{ whiteSpace: "normal", maxWidth: "170px" }}>
                        {items.map((i) => (i.fromPackage ? `${i.name} (package)` : i.name)).join(", ") || "Service"}
                      </span>
                    </td>
                    <td className={styles.textRight}>
                      <strong className={styles.tdTitle}>{money(getInvoiceTotal(inv))}</strong>
                      <small className={styles.tdSub}>{fromPackage && getInvoiceTotal(inv) === 0 ? "Paid by package" : `${formatTime(inv.__at)}, ${formatDate(inv.__at)}`}</small>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </SurfacePanel>
  );
};

const HqStoreTable = ({ stores }) => (
  <SurfacePanel title="Sales by store" subtitle="Revenue and collections across locations." action={`${stores.length} stores`} noPadding>
    {stores.length === 0 ? <EmptyState message="No store-level data for this period." /> : (
      <div className={styles.tableWrapper}>
        <table className={styles.dataTable}>
          <thead>
            <tr>
              <th className={styles.textLeft}>Store</th>
              <th className={styles.textRight}>Total sales</th>
              <th className={styles.textRight}>Fresh collection</th>
              <th className={styles.textRight}>Memberships + packages</th>
              <th className={styles.textRight}>Package services</th>
              <th className={styles.textLeft}>Top performer</th>
            </tr>
          </thead>
          <tbody>
            {stores.map((store, index) => (
              <tr key={store.id}>
                <td className={styles.textLeft}>
                  <div className={styles.tdFlex}>
                    <span className={styles.storeAvatar} style={{ background: index === 0 ? C.primarySoft : C.canvas, color: index === 0 ? C.primary : C.muted }}>
                      <Store size={12} />
                    </span>
                    <div>
                      <strong className={styles.tdTitle}>{store.name}</strong>
                      <small className={styles.tdSub}>{store.appointmentCount} appts, {store.invoiceCount} bills</small>
                    </div>
                  </div>
                </td>
                <td className={styles.textRight}>
                  <strong className={styles.tdTitle}>{money(store.revenue)}</strong>
                  <small className={styles.tdSub} style={{ color: C.primary }}>{store.salesShare.toFixed(1)}% share</small>
                </td>
                <td className={styles.textRight}>
                  <strong className={styles.tdTitle} style={{ color: C.green }}>{money(store.actualCollection)}</strong>
                  <small className={styles.tdSub}>Cash, UPI, card</small>
                </td>
                <td className={styles.textRight}>
                  <strong className={styles.tdTitle}>{money(store.membershipSales)}</strong>
                  <small className={styles.tdSub}>{store.packagesSold > 0 ? `Packages ${money(store.packagesSold)}` : `Avg bill ${money(store.avgTicket)}`}</small>
                </td>
                <td className={styles.textRight}>
                  <strong className={styles.tdTitle}>{store.packageServicesCount ? money(store.packageServicesValue) : "—"}</strong>
                  <small className={styles.tdSub}>{store.packageServicesCount ? `${Math.round(store.packageServicesCount * 10) / 10} used` : "None used"}</small>
                </td>
                <td className={styles.textLeft}>
                  <strong className={styles.tdTitle}>{store.topStaff}</strong>
                  <small className={styles.tdSub}>{store.topStaffSales ? money(store.topStaffSales) : "No sales"}</small>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </SurfacePanel>
);

const HqStaffTable = ({ staff, unassigned, isHQ }) => {
  const anyPackage = staff.some((p) => p.packageCredit > 0);
  return (
    <SurfacePanel
      title="Top performers"
      subtitle={anyPackage ? "Ranked by sales + package services given. Shared lines are split equally." : "Staff ranked by sales. Shared lines are split equally."}
      action={anyPackage ? "By credit" : "By sales"}
      noPadding
    >
      {staff.length === 0 && !unassigned ? <EmptyState message="No staff performance data." /> : (
        <div className={styles.tableWrapper}>
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th className={styles.textLeft} style={{ width: "30px" }}>#</th>
                <th className={styles.textLeft}>Staff</th>
                <th className={styles.textRight}>{anyPackage ? "Credit" : "Sales"}</th>
                <th className={styles.textCenter}>Bills</th>
              </tr>
            </thead>
            <tbody>
              {staff.slice(0, 8).map((person, index) => (
                <tr key={`${person.storeId}-${person.name}`}>
                  <td className={styles.textLeft} style={{ color: index < 3 ? C.primary : C.muted, fontWeight: 850 }}>{String(index + 1).padStart(2, "0")}</td>
                  <td className={styles.textLeft}>
                    <strong className={styles.tdTitle}>{person.name}</strong>
                    <small className={styles.tdSub}>
                      {isHQ ? person.storeName : `${Math.round(person.services * 10) / 10} services`}
                      {person.packageServices > 0 ? `, ${Math.round(person.packageServices * 10) / 10} from packages` : ""}
                    </small>
                  </td>
                  <td className={styles.textRight}>
                    <strong className={styles.tdTitle}>{money(person.credit)}</strong>
                    {person.packageCredit > 0 && <small className={styles.tdSub}>Sales {money(person.sales)} + pkg {money(person.packageCredit)}</small>}
                  </td>
                  <td className={styles.textCenter}><span className={styles.tdSub}>{person.bills}</span></td>
                </tr>
              ))}
              {unassigned > 0 && (
                <tr>
                  <td className={styles.textLeft} style={{ color: C.muted }}>—</td>
                  <td className={styles.textLeft}>
                    <strong className={styles.tdTitle} style={{ color: C.muted }}>Without staff</strong>
                    <small className={styles.tdSub}>Lines with no "Served by"</small>
                  </td>
                  <td className={styles.textRight}><strong className={styles.tdTitle} style={{ color: C.muted }}>{money(unassigned)}</strong></td>
                  <td className={styles.textCenter}><span className={styles.tdSub}>—</span></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </SurfacePanel>
  );
};

const HqServiceTable = ({ services }) => (
  <SurfacePanel title="Top services" subtitle="Highest revenue services (memberships not included). Package services count as done, ₹0 revenue." action="Top 8">
    {services.length === 0 ? <EmptyState message="No service data." /> : (
      <div className={styles.serviceGrid}>
        {services.map((service) => (
          <div key={service.name} className={styles.serviceRow}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className={styles.serviceHeader}>
                <strong className={styles.serviceName}>{service.name}</strong>
                <span className={styles.serviceStats}>
                  {Math.round(service.bookings * 10) / 10} done, {money(service.revenue)}
                  {service.fromPackages > 0 ? ` (${Math.round(service.fromPackages * 10) / 10} from packages)` : ""}
                </span>
              </div>
              <div className={styles.progressBarBg}>
                <span className={styles.progressBarFill} style={{ width: `${service.share}%`, background: C.primary }} />
              </div>
            </div>
            <span className={styles.servicePercent}>{service.share}%</span>
          </div>
        ))}
      </div>
    )}
  </SurfacePanel>
);

/** Panels in rows of two; an odd last panel takes the full width (hidden panels leave no gap) */
const PanelRows = ({ panels }) => {
  const list = panels.filter(Boolean);
  const rows = [];
  for (let i = 0; i < list.length; i += 2) rows.push(list.slice(i, i + 2));
  return rows.map((row, i) => (row.length === 2
    ? <div key={i} className={styles.twoColumnGrid} style={{ marginTop: "16px" }}>{row}</div>
    : <div key={i} style={{ marginTop: "16px" }}>{row[0]}</div>));
};

/** Sales graph | collection | live board, each only with access */
const SalesRow = ({ data, perm, live, onOpenAppointments, onWhatsApp, onOpenBill }) => {
  const showLive = perm.liveBoard && live?.hasContent;
  const feed = showLive ? <LiveFeed feed={live} onOpenCalendar={onOpenAppointments} onWhatsApp={onWhatsApp} onOpenBill={onOpenBill} /> : null;
  if (perm.financial && showLive) {
    return (
      <div className={ax.salesRow}>
        <div className={styles.graphWrapper}><TrendGraph data={data.trendData} /></div>
        <CollectionBreakdown data={data.collections} />
        {feed}
      </div>
    );
  }
  if (perm.financial) {
    return (
      <div className={styles.twoColumnGrid}>
        <div className={styles.graphWrapper}><TrendGraph data={data.trendData} /></div>
        <CollectionBreakdown data={data.collections} />
      </div>
    );
  }
  if (showLive) return <div style={{ maxWidth: 480 }}>{feed}</div>;
  return null;
};

const Metrics = ({ data, isHQ, perm }) => {
  const cards = [];
  const m = data.metrics;
  if (perm.financial) {
    cards.push(
      <HqMetric key="sales" icon={IndianRupee} label="Total sales" value={money(m.totalRevenue)} note="Completed bills" tone="primary" />,
      <HqMetric key="excl" icon={MinusCircle} label="Sales (excl. advance used)" value={money(m.revenueWithoutMembership)} note="Without prepaid/khata" tone="primary" />,
      <HqMetric key="adv" icon={WalletCards} label="Prepaid + khata used" value={money(m.membershipUsed)} note="Paid from advance" tone="pink" />,
      <HqMetric key="fresh" icon={Activity} label="Fresh collection" value={money(m.freshCollection)} note="Cash + UPI + card" tone="green" />,
      <HqMetric key="mem" icon={Crown} label="Memberships sold" value={money(m.membershipSales)} note="Prepaid + discount plans" tone="pink" />,
    );
    if (m.packagesSoldCount > 0 || m.packageServicesCount > 0) {
      cards.push(
        <HqMetric key="pkgSold" icon={PackageOpen} label="Packages sold" value={money(m.packagesSold)} note={`${Math.round(m.packagesSoldCount * 10) / 10} sold`} tone="primary" />,
        <HqMetric
          key="pkgUsed"
          icon={PackageCheck}
          label="Package services used"
          value={money(m.packageServicesValue)}
          note={`${Math.round(m.packageServicesCount * 10) / 10} done, paid earlier`}
          tone="green"
        />,
      );
    }
  }
  if (perm.appointments) {
    cards.push(
      <HqMetric
        key="appts"
        icon={CalendarDays}
        label="Appointments"
        value={m.appointments}
        note={data.appt.total ? `${data.appt.upcoming} upcoming, ${data.appt.done} done` : (isHQ ? "All stores" : "No bookings")}
        tone="grey"
      />,
    );
    if (data.appt.total > 0) {
      cards.push(
        <HqMetric
          key="booked"
          icon={CalendarCheck}
          label="Booked value"
          value={money(data.appt.bookedValue)}
          note={data.appt.next ? `Next ${prettyTime(data.appt.next.startTime)}, ${data.appt.next.clientName}` : `${data.appt.cancelled} cancelled / no show`}
          tone="primary"
        />,
      );
    }
  }
  if (isHQ && (perm.financial || perm.appointments)) {
    cards.push(<HqMetric key="stores" icon={Building2} label="Stores active" value={`${data.activeStoreCount}/${data.storeStats.length}`} note="Sales or bookings" tone="grey" />);
  }
  if (perm.financial || perm.recentClients || perm.appointments) {
    cards.push(<HqMetric key="cust" icon={UsersRound} label="Customers served" value={isHQ ? data.totalCustomerCount : m.footfall} note="Unique in range" tone="primary" />);
  }
  if (!cards.length) return null;
  return (
    <div className={`${isHQ ? styles.metricGridHq : styles.metricGridStore} ${ax.metricsGrid}`} style={{ gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))" }}>
      {cards}
    </div>
  );
};

const StorePerformance = ({ data }) => (
  <SurfacePanel title="Store performance" subtitle="For the selected date range, by completion date.">
    <div className={styles.perfList}>
      <div className={styles.perfRow}><span>Total sales</span><strong>{money(data.metrics.totalRevenue)}</strong></div>
      <div className={styles.perfRow}><span>Sales excl. advance used</span><strong style={{ color: C.primary }}>{money(data.metrics.revenueWithoutMembership)}</strong></div>
      <div className={styles.perfRow}><span>Fresh collection</span><strong style={{ color: C.green }}>{money(data.metrics.freshCollection)}</strong></div>
      <div className={styles.perfRow}><span>Prepaid + khata used</span><strong style={{ color: C.pink }}>{money(data.metrics.membershipUsed)}</strong></div>
      <div className={styles.perfRow}><span>Memberships sold</span><strong>{money(data.metrics.membershipSales)}</strong></div>
      {(data.metrics.packagesSoldCount > 0 || data.metrics.packageServicesCount > 0) && (
        <>
          <div className={styles.perfRow}><span>Packages sold ({Math.round(data.metrics.packagesSoldCount * 10) / 10})</span><strong>{money(data.metrics.packagesSold)}</strong></div>
          <div className={styles.perfRow}><span>Package services used ({Math.round(data.metrics.packageServicesCount * 10) / 10})</span><strong style={{ color: C.green }}>{money(data.metrics.packageServicesValue)}</strong></div>
        </>
      )}
      <div className={styles.perfRow}><span>Average bill</span><strong>{money(data.averageTicket)}</strong></div>
      <div className={styles.perfRow}><span>Bills</span><strong>{data.rangeInvoices.length}</strong></div>
      {data.appt.total > 0 && <div className={styles.perfRow}><span>Appointments booked value</span><strong>{money(data.appt.bookedValue)}</strong></div>}
    </div>
  </SurfacePanel>
);

const EnterpriseOverview = ({ data, perm, ...liveProps }) => (
  <>
    <Metrics data={data} isHQ perm={perm} />
    <SalesRow data={data} perm={perm} {...liveProps} />
    {perm.financial && <div style={{ marginTop: "16px" }}><HqStoreTable stores={data.storeStats} /></div>}
    <PanelRows panels={[
      perm.staffMetrics && <HqStaffTable key="staff" staff={data.staffStats} unassigned={data.metrics.unassignedSales} isHQ />,
      perm.financial && <HqServiceTable key="services" services={data.services} />,
      perm.recentClients && <RecentClientsPanel key="recent" invoices={data.rangeInvoices} showPhone={perm.showPhone} />,
    ]} />
  </>
);

const StoreOverview = ({ data, perm, ...liveProps }) => (
  <>
    <Metrics data={data} isHQ={false} perm={perm} />
    <SalesRow data={data} perm={perm} {...liveProps} />
    <PanelRows panels={[
      perm.recentClients && <RecentClientsPanel key="recent" invoices={data.rangeInvoices} showPhone={perm.showPhone} />,
      perm.financial && <HqServiceTable key="services" services={data.services} />,
      perm.staffMetrics && <HqStaffTable key="staff" staff={data.staffStats} unassigned={data.metrics.unassignedSales} isHQ={false} />,
      perm.financial && <StorePerformance key="perf" data={data} />,
    ]} />
  </>
);

/* ============================== page ============================== */
const DashboardEnterprise = () => {
  const navigate = useNavigate();
  const { can } = useAccess();

  // What this user may see on the dashboard (booleans, so effects do not re-run on every render)
  const pFinancial = can("dashboard.view_financial_metrics");
  const pStaff = can("dashboard.view_staff_metrics");
  const pRecent = can("dashboard.view_recent_clients");
  const pLive = can("dashboard.view_live_board");
  const pAppts = can("dashboard.view_appointment_metrics");
  const pWhatsApp = can("dashboard.send_whatsapp");
  const pCalendar = can("appointments.view");
  const pOpenBill = can("billing.view_bill") || can("billing.view_history");
  const pPhone = can("customers.view_phone");
  const pSettings = can("settings.view");
  const perm = useMemo(() => ({
    financial: pFinancial, staffMetrics: pStaff, recentClients: pRecent, liveBoard: pLive, appointments: pAppts, showPhone: pPhone,
  }), [pFinancial, pStaff, pRecent, pLive, pAppts, pPhone]);
  const needBills = pFinancial || pStaff || pRecent || pLive;
  const needAppts = pAppts || pLive;
  const nothingAllowed = !pFinancial && !pStaff && !pRecent && !pLive && !pAppts;
  const storeContext = useStore() || {};
  const { activeStore } = storeContext;
  const token = typeof localStorage !== "undefined" ? localStorage.getItem("smiify_token") : "";
  const [rawInvoices, setRawInvoices] = useState([]);
  const [rawAppointments, setRawAppointments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [dateRange, setDateRange] = useState({ startDate: getTodayDate(), endDate: getTodayDate(), label: "Today" });

  const hqMode = isHeadOffice(activeStore);
  const contextStoreSources = useMemo(() => ({
    stores: storeContext?.stores, allStores: storeContext?.allStores, storeList: storeContext?.storeList,
    availableStores: storeContext?.availableStores, storeOptions: storeContext?.storeOptions,
  }), [storeContext?.allStores, storeContext?.availableStores, storeContext?.storeList, storeContext?.storeOptions, storeContext?.stores]);
  const linkedStores = useMemo(() => storeListFromContext(contextStoreSources, activeStore), [activeStore, contextStoreSources]);
  const storeTargets = useMemo(() => (hqMode ? linkedStores : activeStore ? [activeStore] : []), [activeStore, hqMode, linkedStores]);

  const fetchDashboardData = useCallback(async (signal, { silent = false } = {}) => {
    if (!activeStore || !token) {
      if (silent) return;
      setRawInvoices([]); setRawAppointments([]);
      setLoadError(!activeStore ? "Select a store or Enterprise HQ to view the dashboard." : "Your session has expired. Please sign in again.");
      setIsLoading(false);
      return;
    }

    if (!needBills && !needAppts) {
      setRawInvoices([]); setRawAppointments([]);
      if (!silent) { setLoadError(""); setIsLoading(false); }
      return;
    }

    if (!silent) { setIsLoading(true); setLoadError(""); }
    try {
      const enterpriseId = getEnterpriseId(activeStore);
      let invoices = [];
      let appointments = [];
      const warnings = [];

      if (hqMode && linkedStores.length) {
        const responses = await Promise.allSettled(linkedStores.map(async (target) => {
          const storeId = getStoreId(target);
          const [invoiceRows, appointmentRows] = await Promise.all([
            needBills ? requestRows(INVOICES_API, { storeId, status: "completed" }, token, signal, storeId) : [],
            needAppts ? requestRows(APPOINTMENTS_API, { storeId }, token, signal, storeId) : [],
          ]);
          return { invoiceRows: markedRows(invoiceRows, target, "invoice"), appointmentRows: markedRows(appointmentRows, target, "appointment") };
        }));
        responses.forEach((r) => {
          if (r.status === "fulfilled") {
            invoices = invoices.concat(r.value.invoiceRows);
            appointments = appointments.concat(r.value.appointmentRows);
          } else if (r.reason?.name !== "AbortError") {
            warnings.push(r.reason?.message || "One store could not be synced.");
          }
        });
      } else {
        const params = hqMode
          ? { scope: "all", allStores: "true", status: "completed", ...(enterpriseId ? { enterpriseId } : {}) }
          : { storeId: getStoreId(activeStore), status: "completed" };
        const appointmentParams = hqMode
          ? { scope: "all", allStores: "true", ...(enterpriseId ? { enterpriseId } : {}) }
          : { storeId: getStoreId(activeStore) };
        const requestStoreId = hqMode ? "" : getStoreId(activeStore);
        const [invoiceRows, appointmentRows] = await Promise.all([
          needBills ? requestRows(INVOICES_API, params, token, signal, requestStoreId) : [],
          needAppts ? requestRows(APPOINTMENTS_API, appointmentParams, token, signal, requestStoreId) : [],
        ]);
        invoices = markedRows(invoiceRows, hqMode ? null : activeStore, "invoice");
        appointments = markedRows(appointmentRows, hqMode ? null : activeStore, "appointment");
      }

      // Only completed bills belong on the dashboard (in case the API ignores ?status)
      invoices = invoices.filter((inv) => !inv.status || String(inv.status).toLowerCase() === "completed");

      if (!invoices.length && !appointments.length && hqMode && !linkedStores.length) {
        warnings.push("HQ returned no records. Check that the backend allows scope=all for this account.");
      }
      setRawInvoices(invoices);
      setRawAppointments(appointments);
      if (warnings.length && !silent) setLoadError(warnings[0]);
    } catch (error) {
      if (silent) return; // keep showing the last data; next refresh will try again
      if (error.name !== "AbortError") {
        console.error("Dashboard fetch failed:", error);
        setRawInvoices([]); setRawAppointments([]);
        setLoadError(error.message || "Unable to load dashboard data.");
      }
    } finally {
      if (!silent && !signal.aborted) setIsLoading(false);
    }
  }, [activeStore, hqMode, linkedStores, token, needBills, needAppts]);

  useEffect(() => {
    const controller = new AbortController();
    fetchDashboardData(controller.signal);
    return () => controller.abort();
  }, [fetchDashboardData, refreshKey]);

  // Live: refresh quietly every 90 s while the tab is visible, and right away when you come back to it
  useEffect(() => {
    let controller = null;
    const refresh = () => {
      if (document.visibilityState !== "visible") return;
      controller?.abort();
      controller = new AbortController();
      fetchDashboardData(controller.signal, { silent: true });
    };
    const id = setInterval(refresh, 90000);
    const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVisible); controller?.abort(); };
  }, [fetchDashboardData]);

  // Clock for countdowns ("in 25 min") on the live board
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);

  const liveFeed = useMemo(() => buildLiveFeed(rawAppointments, (pFinancial || pRecent) ? rawInvoices : [], now, {
    isHQ: hqMode,
    storeNameOf: (row) => getRecordStore(row).name,
  }), [rawAppointments, rawInvoices, now, hqMode, pFinancial, pRecent]);

  const dashboardData = useMemo(() => buildDashboardData({
    invoices: rawInvoices,
    appointments: rawAppointments,
    dateRange,
    isHQ: hqMode,
    storeTargets: storeTargets.length ? storeTargets : [{ id: "unassigned", name: "Unassigned store" }],
  }), [dateRange, hqMode, rawAppointments, rawInvoices, storeTargets]);

  const selectedName = hqMode ? "Enterprise HQ, all stores" : getStoreName(activeStore, "Selected store");

  /** WhatsApp to the client from the dashboard, and save that it was sent */
  const sendAppointmentWhatsApp = useCallback((appointment, type) => {
    const store = hqMode ? (appointment.__dashboardStore || activeStore) : activeStore;
    openWhatsApp(appointment.clientPhone, clientMessage(type, appointment, store));
    const storeId = String(appointment.storeId || appointment.__dashboardStoreId || getStoreId(activeStore) || "");
    if (!appointment._id || !storeId) return;
    fetch(`${APPOINTMENTS_API}/${appointment._id}/notifications`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, "x-store-id": storeId },
      body: JSON.stringify({ type, audience: "CLIENT", to: appointment.clientPhone }),
    }).catch(() => {});
  }, [activeStore, hqMode, token]);

  const liveProps = {
    live: liveFeed,
    onOpenAppointments: pCalendar ? () => navigate("/appointments") : null,
    onWhatsApp: pWhatsApp ? sendAppointmentWhatsApp : null,
    onOpenBill: pOpenBill ? (invoice) => invoice?._id && navigate(`/create-bill/${invoice._id}/preview`) : null,
  };

  return (
    <div className={styles.dashboardContainer}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h1 className={styles.pageTitle}>Overview</h1>
            <span className={styles.badgeLabel}>
              {hqMode ? <Building2 size={11} /> : <Store size={11} />}
              {hqMode ? "Enterprise view" : "Store view"}
            </span>
          </div>
          <p className={styles.pageSubtitle}>Business insights for <strong>{selectedName}</strong>.</p>
        </div>
        <div className={styles.headerActions}>
          <GlobalDateFilter startDate={dateRange.startDate} endDate={dateRange.endDate} defaultLabel={dateRange.label} onChange={(range) => setDateRange(range)} />
          <button type="button" onClick={() => setRefreshKey((k) => k + 1)} title="Refresh dashboard" aria-label="Refresh dashboard" className={styles.iconBtn}>
            <RefreshCcw size={14} />
          </button>
          {!hqMode && pSettings && (
            <button type="button" className={styles.primaryBtn} onClick={() => navigate("/settings")}><Settings size={12} /> Store Settings</button>
          )}
        </div>
      </div>

      {loadError && <ErrorState message={loadError} onRetry={() => setRefreshKey((k) => k + 1)} />}

      {nothingAllowed ? (
        <div className={styles.emptyStateContainer} style={{ padding: "48px 20px", textAlign: "center" }}>
          <strong style={{ display: "block", fontSize: 14, marginBottom: 6 }}>Welcome{activeStore ? ` to ${getStoreName(activeStore, "your branch")}` : ""}</strong>
          Use the menu on the left to start. Ask your manager if you need to see sales or appointments here.
        </div>
      ) : isLoading ? (
        <div className={styles.loaderScreen}>
          <Loader2 className={styles.spin} size={24} color={C.primary} />
          <span>Syncing live {hqMode ? "enterprise" : "store"} dashboard...</span>
        </div>
      ) : hqMode ? (
        <EnterpriseOverview data={dashboardData} perm={perm} {...liveProps} />
      ) : (
        <StoreOverview data={dashboardData} perm={perm} {...liveProps} />
      )}

      {!isLoading && !nothingAllowed && pFinancial && (
        <div className={styles.footerNote}>
          <CheckCircle2Icon size={12} /> Bills counted on the day they were completed. {hqMode ? `${dashboardData.storeStats.length} connected stores` : selectedName}, {dateRange.label || "selected period"}.
        </div>
      )}
    </div>
  );
};

export default DashboardEnterprise;